"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MultiUserCombobox } from "@/components/ui/MultiUserCombobox";
import { useTenancy } from "@/contexts/TenancyContext";
import { HelpSidebar } from "@/app/components/onboarding/HelpSidebar";
import { X, Plus, Check, Mail } from "lucide-react";

export default function ModalUserSelectionExample() {
  const [open, setOpen] = React.useState(true);
  const [selected, setSelected] = React.useState<string[]>([]);
  const [additionalMembers, setAdditionalMembers] = React.useState<{ id: string; name: string; email: string }[]>([]);
  const [newMember, setNewMember] = React.useState({ name: "", email: "" });
  const [showHelp, setShowHelp] = React.useState(true);
  const [isSendingInvites, setIsSendingInvites] = React.useState(false);
  const [invitesSent, setInvitesSent] = React.useState(false);
  const { userIdToName } = useTenancy();

  const removeSelected = (id: string) => setSelected(prev => prev.filter(v => v !== id));
  const removeAdditional = (id: string) => setAdditionalMembers(prev => prev.filter(v => v.id !== id));

  const handleAddMember = () => {
    if (!newMember.name || !newMember.email) return;
    setAdditionalMembers(prev => [...prev, { id: Date.now().toString(), name: newMember.name, email: newMember.email }]);
    setNewMember({ name: "", email: "" });
  };

  const handleSendInvites = async () => {
    setIsSendingInvites(true);
    await new Promise(r => setTimeout(r, 1200));
    setIsSendingInvites(false);
    setInvitesSent(true);
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open User Selection Modal</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl! w-6xl! bg-transparent shadow-none border-0 p-0">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col" style={{ minHeight: "70vh" }}>
            <div className="p-6 pb-4 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Select Your Compliance Team</h2>
              <p className="mt-2 text-muted-foreground">Invite existing users or add new members by name and email.</p>
            </div>

            <div className="p-6 space-y-6 flex-1 overflow-y-auto">
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-foreground">Team Members</h3>
                <div className="space-y-3">
                  {selected.map(id => {
                    const label = userIdToName?.[id] || id;
                    const initials = label.split(" ").map(n => n[0]).join("");
                    return (
                      <div key={id} className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/15 transition-all duration-200 shadow-sm">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center text-primary border border-primary/30">
                            <span className="text-sm font-semibold">{initials}</span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-foreground">{label}</p>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/20 text-primary border border-primary/30 mt-1">Existing User</span>
                          </div>
                        </div>
                        <button type="button" onClick={() => removeSelected(id)} className="text-muted-foreground hover:text-destructive transition-colors rounded-lg p-2 hover:bg-white/5" aria-label="Remove">
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    );
                  })}
                  {additionalMembers.map(m => (
                    <div key={m.id} className="flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/15 transition-all duration-200 shadow-sm">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center text-primary border border-primary/30">
                          <span className="text-sm font-semibold">{m.name.split(" ").map(n => n[0]).join("")}</span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-foreground">{m.name}</p>
                          <p className="text-xs text-muted-foreground">{m.email}</p>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/20 text-primary border border-primary/30 mt-1">Added Manually</span>
                        </div>
                      </div>
                      <button type="button" onClick={() => removeAdditional(m.id)} className="text-muted-foreground hover:text-destructive transition-colors rounded-lg p-2 hover:bg-white/5" aria-label="Remove">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                  {selected.length + additionalMembers.length === 0 && (
                    <div className="text-sm text-muted-foreground">No members added yet.</div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-medium text-foreground">Add Members</h4>
                <MultiUserCombobox selected={selected} setSelected={setSelected} placeholder="Search existing users..." />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-white/5 backdrop-blur-sm border border-white/20 rounded-xl p-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-foreground mb-1">Name</label>
                    <Input id="name" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} className="bg-white/10 border-white/20" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-foreground mb-1">Email</label>
                    <Input id="email" type="email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} className="bg-white/10 border-white/20" />
                  </div>
                  <div className="md:col-span-2">
                    <Button type="button" variant="outline" onClick={handleAddMember} disabled={!newMember.name || !newMember.email} className="backdrop-blur-sm border-white/20 hover:bg-white/10">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Team Member
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur-sm px-6 py-4 flex justify-between items-center">
              <Button type="button" variant="outline" onClick={() => setShowHelp(true)} className="backdrop-blur-sm border-white/20 hover:bg-white/10">Help</Button>
              <div className="flex space-x-3">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="backdrop-blur-sm border-white/20 hover:bg-white/10">Close</Button>
                <Button type="button" onClick={handleSendInvites} disabled={isSendingInvites || invitesSent || (selected.length + additionalMembers.length) === 0} className="flex items-center bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20">
                  {isSendingInvites ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                      Sending...
                    </>
                  ) : invitesSent ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Invites Sent
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Invites
                    </>
                  )}
                </Button>
                <Button type="button" onClick={() => setOpen(false)} className="min-w-[120px] bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20">Continue</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <HelpSidebar
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        faqs={[
          { question: 'Who should I invite?', answer: 'Invite key stakeholders across departments who will contribute to compliance activities.' },
          { question: 'Can I add guests?', answer: 'Yes, you can add users by email even if they are not in your tenancy yet.' },
        ]}
        videoTitle="Building Your Compliance Team"
        videoUrl="https://www.youtube.com/embed/example-team-video"
      />
    </div>
  );
}
